/**
 * skin_curvature — the per-vertex CURVATURE and THICKNESS the skin lighting reads (shader gaps program round 2,
 * agent D, 2026-09-29).
 *
 * PURE (no three): positions, normals and an index in, two numbers per vertex out, both in the geometry's OWN units
 * (curvature in units⁻¹, thickness in units). The shader converts to millimetres per pixel (below).
 *
 * WHY NOT THE SCREEN DERIVATIVE. Penner's pre-integrated skin (SIGGRAPH 2011) takes curvature per pixel as
 * |d N| / |d P| over the pixel quad. On a served Meshy rig that estimate is noise: the normals are 8-bit
 * (`NORMAL:i8_norm`) and a third of the vertices are split along UV seams (the steward: 201,630 vertices, 144,469
 * positions), so the quad that straddles a seam reads a normal jump over a fraction of a millimetre — the curvature of
 * an ear rim on a flat cheek. The bed pictured it: red sparkles across a back-lit face (the thickness proxy made those
 * pixels paper thin). So both terms are BAKED, as production pre-integrated skin does (Penner's talk: a pre-computed
 * curvature where the derivative misbehaves; Barré-Brisebois & Bouchard, GDC 2011, "Approximating Translucency": a
 * baked LOCAL THICKNESS per vertex / texel for the transmitted light):
 *   1. WELD by position (split seam vertices become one — dequantised positions compare exactly);
 *   2. the welded normal = the mean of its split normals (the smooth surface the seam cut apart);
 *   3. CURVATURE per edge along the edge (Rusinkiewicz 2004's per-edge normal curvature)
 *        κ_e = |(n_a − n_b) · (p_a − p_b)| / |p_a − p_b|²,
 *      per vertex the LENGTH-WEIGHTED mean over its edges, Σ κ_e·|e| / Σ |e| (a sliver edge weighs as little as it is
 *      long); the magnitude, because a concave crease scatters as a convex rim does;
 *   4. THICKNESS per vertex: one ray from the vertex INTO the body (−n) to the nearest surface it meets, capped — an
 *      ear is 2–4 mm, a cheek is past the cap (the uniform-grid march below; rig actors receive no shadow map, so the
 *      shadow-depth thickness of Jimenez et al. 2010 is not available);
 *   5. two Jacobi smoothing passes over the welded neighbours for both (the diffusion profile blurs on a mm scale).
 * The shader turns local units into metres per pixel with the ratio of the skinned view-space position's derivative to
 * the raw geometry position's derivative (both continuous across seams, both exact per triangle), so a fit-scaled crew
 * body or the rig's own 0.01 node scale needs no CPU measurement.
 */

export interface SkinGeometryBakeInput {
  /** xyz per vertex, dequantised, in the geometry's local units */
  positions: ArrayLike<number>;
  /** xyz per vertex (unit, or near it — 8-bit normals are fine) */
  normals: ArrayLike<number>;
  /** triangle list; null = non-indexed (every 3 vertices a triangle) */
  index: ArrayLike<number> | null;
  vertexCount: number;
  /** the thickness ray's reach, local units (default 3 % of the bounding box's largest side) */
  thicknessCap?: number;
  /** Jacobi smoothing passes over the welded neighbours (default 2) */
  smoothPasses?: number;
}

export interface SkinGeometryBake {
  /** two per ORIGINAL vertex: [curvature (units⁻¹), thickness (units)]; split vertices of one weld share them */
  packed: Float32Array;
  welded: number;
  thicknessCap: number;
  /** thickness rays that met a surface inside the cap */
  hits: number;
  ms: number;
}

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** The per-vertex curvature and thickness (both in local units). */
export function bakeSkinGeometry(inp: SkinGeometryBakeInput): SkinGeometryBake {
  const t0 = now();
  const { positions: P, normals: N, vertexCount: n } = inp;
  const passes = inp.smoothPasses ?? 2;
  let mnx = Infinity, mny = Infinity, mnz = Infinity, mxx = -Infinity, mxy = -Infinity, mxz = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = P[i * 3], y = P[i * 3 + 1], z = P[i * 3 + 2];
    if (x < mnx) mnx = x; if (x > mxx) mxx = x;
    if (y < mny) mny = y; if (y > mxy) mxy = y;
    if (z < mnz) mnz = z; if (z > mxz) mxz = z;
  }
  const ex = mxx - mnx, ey = mxy - mny, ez = mxz - mnz;
  const diag = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1;
  const cap = inp.thicknessCap ?? Math.max(ex, ey, ez) * 0.03;
  // ── 1. weld (quantum = a millionth of the diagonal; open-addressed table) ──
  const q = diag * 1e-6;
  const qx = new Int32Array(n), qy = new Int32Array(n), qz = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    qx[i] = Math.round((P[i * 3] - mnx) / q);
    qy[i] = Math.round((P[i * 3 + 1] - mny) / q);
    qz[i] = Math.round((P[i * 3 + 2] - mnz) / q);
  }
  let cap2 = 1; while (cap2 < n * 2) cap2 <<= 1;
  const table = new Int32Array(cap2);
  const weld = new Int32Array(n);
  const rep = new Int32Array(n);
  let W = 0;
  for (let i = 0; i < n; i++) {
    let h = (Math.imul(qx[i], 73856093) ^ Math.imul(qy[i], 19349663) ^ Math.imul(qz[i], 83492791)) & (cap2 - 1);
    for (;;) {
      const r = table[h];
      if (r === 0) { table[h] = i + 1; weld[i] = W; rep[W] = i; W++; break; }
      const j = r - 1;
      if (qx[j] === qx[i] && qy[j] === qy[i] && qz[j] === qz[i]) { weld[i] = weld[j]; break; }
      h = (h + 1) & (cap2 - 1);
    }
  }
  // ── 2. welded positions and normals ──
  const wp = new Float64Array(W * 3), wn = new Float64Array(W * 3);
  for (let w = 0; w < W; w++) { const i = rep[w]; wp[w * 3] = P[i * 3]; wp[w * 3 + 1] = P[i * 3 + 1]; wp[w * 3 + 2] = P[i * 3 + 2]; }
  for (let i = 0; i < n; i++) { const w = weld[i]; wn[w * 3] += N[i * 3]; wn[w * 3 + 1] += N[i * 3 + 1]; wn[w * 3 + 2] += N[i * 3 + 2]; }
  for (let w = 0; w < W; w++) {
    const l = Math.sqrt(wn[w * 3] ** 2 + wn[w * 3 + 1] ** 2 + wn[w * 3 + 2] ** 2) || 1;
    wn[w * 3] /= l; wn[w * 3 + 1] /= l; wn[w * 3 + 2] /= l;
  }
  // the welded triangle list
  const idx = inp.index;
  const T = idx ? Math.floor(idx.length / 3) : Math.floor(n / 3);
  const tri = new Int32Array(T * 3);
  for (let t = 0; t < T; t++) {
    tri[t * 3] = weld[idx ? idx[t * 3] : t * 3];
    tri[t * 3 + 1] = weld[idx ? idx[t * 3 + 1] : t * 3 + 1];
    tri[t * 3 + 2] = weld[idx ? idx[t * 3 + 2] : t * 3 + 2];
  }
  // ── 3. curvature ──
  const num = new Float64Array(W), den = new Float64Array(W);
  const edge = (a: number, b: number): void => {
    if (a === b) return;
    const dx = wp[a * 3] - wp[b * 3], dy = wp[a * 3 + 1] - wp[b * 3 + 1], dz = wp[a * 3 + 2] - wp[b * 3 + 2];
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (!(len > 0)) return;
    const dn = (wn[a * 3] - wn[b * 3]) * dx + (wn[a * 3 + 1] - wn[b * 3 + 1]) * dy + (wn[a * 3 + 2] - wn[b * 3 + 2]) * dz;
    const k = Math.abs(dn) / len;
    num[a] += k; num[b] += k; den[a] += len; den[b] += len;
  };
  for (let t = 0; t < T; t++) { const a = tri[t * 3], b = tri[t * 3 + 1], c = tri[t * 3 + 2]; edge(a, b); edge(b, c); edge(c, a); }
  let curv = new Float64Array(W);
  for (let w = 0; w < W; w++) curv[w] = den[w] > 0 ? num[w] / den[w] : 0;
  // ── 4. thickness: a uniform grid of the triangles, one ray per welded vertex ──
  let hcell = cap * 0.15;
  const dims = (): [number, number, number] => [Math.max(1, Math.ceil(ex / hcell) + 1), Math.max(1, Math.ceil(ey / hcell) + 1), Math.max(1, Math.ceil(ez / hcell) + 1)];
  let [gx, gy, gz] = dims();
  while (gx * gy * gz > 4_000_000) { hcell *= 1.5; [gx, gy, gz] = dims(); }
  const cells = gx * gy * gz;
  const cellOf = (v: number, mn: number, g: number): number => Math.min(g - 1, Math.max(0, Math.floor((v - mn) / hcell)));
  const start = new Int32Array(cells + 1);
  const triBox = new Int32Array(T * 6);
  for (let t = 0; t < T; t++) {
    const a = tri[t * 3] * 3, b = tri[t * 3 + 1] * 3, c = tri[t * 3 + 2] * 3;
    const x0 = cellOf(Math.min(wp[a], wp[b], wp[c]), mnx, gx), x1 = cellOf(Math.max(wp[a], wp[b], wp[c]), mnx, gx);
    const y0 = cellOf(Math.min(wp[a + 1], wp[b + 1], wp[c + 1]), mny, gy), y1 = cellOf(Math.max(wp[a + 1], wp[b + 1], wp[c + 1]), mny, gy);
    const z0 = cellOf(Math.min(wp[a + 2], wp[b + 2], wp[c + 2]), mnz, gz), z1 = cellOf(Math.max(wp[a + 2], wp[b + 2], wp[c + 2]), mnz, gz);
    triBox[t * 6] = x0; triBox[t * 6 + 1] = x1; triBox[t * 6 + 2] = y0; triBox[t * 6 + 3] = y1; triBox[t * 6 + 4] = z0; triBox[t * 6 + 5] = z1;
    for (let z = z0; z <= z1; z++) for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) start[(z * gy + y) * gx + x + 1]++;
  }
  for (let i = 0; i < cells; i++) start[i + 1] += start[i];
  const fill = start.slice(0, cells);
  const list = new Int32Array(start[cells]);
  for (let t = 0; t < T; t++) {
    const x0 = triBox[t * 6], x1 = triBox[t * 6 + 1], y0 = triBox[t * 6 + 2], y1 = triBox[t * 6 + 3], z0 = triBox[t * 6 + 4], z1 = triBox[t * 6 + 5];
    for (let z = z0; z <= z1; z++) for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) list[fill[(z * gy + y) * gx + x]++] = t;
  }
  const stamp = new Int32Array(T).fill(-1);
  const tMin = diag * 2e-5;
  const thick = new Float64Array(W);
  let hits = 0;
  for (let w = 0; w < W; w++) {
    const ox = wp[w * 3], oy = wp[w * 3 + 1], oz = wp[w * 3 + 2];
    const dx = -wn[w * 3], dy = -wn[w * 3 + 1], dz = -wn[w * 3 + 2];
    let best = cap;
    // Amanatides–Woo over the cells the ray crosses inside [0, cap]
    let cx = cellOf(ox, mnx, gx), cy = cellOf(oy, mny, gy), cz = cellOf(oz, mnz, gz);
    const sx = dx > 0 ? 1 : -1, sy = dy > 0 ? 1 : -1, sz = dz > 0 ? 1 : -1;
    const bound = (c: number, s: number, mn: number): number => mn + (c + (s > 0 ? 1 : 0)) * hcell;
    let tx = dx !== 0 ? (bound(cx, sx, mnx) - ox) / dx : Infinity;
    let ty = dy !== 0 ? (bound(cy, sy, mny) - oy) / dy : Infinity;
    let tz = dz !== 0 ? (bound(cz, sz, mnz) - oz) / dz : Infinity;
    const dtx = dx !== 0 ? hcell / Math.abs(dx) : Infinity, dty = dy !== 0 ? hcell / Math.abs(dy) : Infinity, dtz = dz !== 0 ? hcell / Math.abs(dz) : Infinity;
    for (let guard = 0; guard < 64; guard++) {
      const cell = (cz * gy + cy) * gx + cx;
      for (let k = start[cell], e = start[cell + 1]; k < e; k++) {
        const t = list[k];
        if (stamp[t] === w) continue;
        stamp[t] = w;
        const a = tri[t * 3], b = tri[t * 3 + 1], c = tri[t * 3 + 2];
        if (a === w || b === w || c === w) continue; // the vertex's own fan
        // Möller–Trumbore
        const ax = wp[a * 3], ay = wp[a * 3 + 1], az = wp[a * 3 + 2];
        const e1x = wp[b * 3] - ax, e1y = wp[b * 3 + 1] - ay, e1z = wp[b * 3 + 2] - az;
        const e2x = wp[c * 3] - ax, e2y = wp[c * 3 + 1] - ay, e2z = wp[c * 3 + 2] - az;
        const px = dy * e2z - dz * e2y, py = dz * e2x - dx * e2z, pz = dx * e2y - dy * e2x;
        const det = e1x * px + e1y * py + e1z * pz;
        if (det > -1e-14 && det < 1e-14) continue;
        const inv = 1 / det;
        const sxv = ox - ax, syv = oy - ay, szv = oz - az;
        const u = (sxv * px + syv * py + szv * pz) * inv;
        if (u < 0 || u > 1) continue;
        const qxv = syv * e1z - szv * e1y, qyv = szv * e1x - sxv * e1z, qzv = sxv * e1y - syv * e1x;
        const v = (dx * qxv + dy * qyv + dz * qzv) * inv;
        if (v < 0 || u + v > 1) continue;
        const tt = (e2x * qxv + e2y * qyv + e2z * qzv) * inv;
        if (tt > tMin && tt < best) best = tt;
      }
      // step to the next cell; stop once the nearest hit lies before this cell's exit or the reach is spent
      const tNext = Math.min(tx, ty, tz);
      if (best <= tNext || tNext >= cap) break;
      if (tx === tNext) { cx += sx; tx += dtx; if (cx < 0 || cx >= gx) break; }
      else if (ty === tNext) { cy += sy; ty += dty; if (cy < 0 || cy >= gy) break; }
      else { cz += sz; tz += dtz; if (cz < 0 || cz >= gz) break; }
    }
    if (best < cap) hits++;
    thick[w] = best;
  }
  // ── 5. smoothing (each triangle edge counted from both sides) ──
  const smooth = (src: Float64Array): Float64Array => {
    let k = src;
    for (let p = 0; p < passes; p++) {
      const sum = new Float64Array(W), cnt = new Float64Array(W);
      for (let t = 0; t < T; t++) {
        const a = tri[t * 3], b = tri[t * 3 + 1], c = tri[t * 3 + 2];
        if (a !== b) { sum[a] += k[b]; cnt[a]++; sum[b] += k[a]; cnt[b]++; }
        if (b !== c) { sum[b] += k[c]; cnt[b]++; sum[c] += k[b]; cnt[c]++; }
        if (c !== a) { sum[c] += k[a]; cnt[c]++; sum[a] += k[c]; cnt[a]++; }
      }
      const next = new Float64Array(W);
      for (let w = 0; w < W; w++) next[w] = cnt[w] > 0 ? 0.5 * k[w] + 0.5 * (sum[w] / cnt[w]) : k[w];
      k = next;
    }
    return k;
  };
  curv = smooth(curv);
  const th = smooth(thick);
  const packed = new Float32Array(n * 2);
  for (let i = 0; i < n; i++) { packed[i * 2] = curv[weld[i]]; packed[i * 2 + 1] = th[weld[i]]; }
  return { packed, welded: W, thicknessCap: cap, hits, ms: +(now() - t0).toFixed(1) };
}

/** Local units⁻¹ → mm⁻¹ at a pixel whose skinned view position moves `metresPerLocal` metres per local unit. */
export function curvatureMmFromLocal(kLocal: number, metresPerLocal: number): number {
  return (kLocal / Math.max(metresPerLocal, 1e-9)) * 0.001;
}

/** Local units → mm at such a pixel. */
export function thicknessMmFromLocal(dLocal: number, metresPerLocal: number): number {
  return dLocal * metresPerLocal * 1000;
}
